<template>
  <div class="page-wrapper mt-3" style="width: 100%">
    <PageTitle title="Группы пользователей" />

    <!-- БЛОК ПОИСКА И ДОБАВЛЕНИЯ -->
    <v-card variant="outlined" class="ma-2 pa-4 bg-white">
      <v-row align="center">
        <v-col cols="12" sm="8" md="6">
          <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            label="Найти группу по названию..."
            variant="outlined"
            density="comfortable"
            hide-details
            clearable
            @input="debounceSearch"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4" md="6" class="text-sm-right">
          <v-btn
            color="deep-orange-darken-1"
            prepend-icon="mdi-plus"
            variant="flat"
            @click="openGroupDialog()"
          >
            Добавить группу
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- СПИСОК ГРУПП (Оформлен в виде аккуратных раскрывающихся карточек) -->
    <div class="ma-2">
      <v-row v-if="filteredGroups.length > 0">
        <v-col cols="12" v-for="group in filteredGroups" :key="group.id">
          <v-card variant="outlined" class="bg-white mb-2">

            <!-- Шапка карточки группы -->
            <v-card-item class="bg-grey-lighten-4 py-3">
              <div class="d-flex justify-space-between align-center w-100">
                <div class="d-flex align-center">
                  <v-icon color="deep-orange-darken-1" class="mr-2" size="large">mdi-account-group-outline</v-icon>
                  <span class="text-h6 font-weight-bold text-deep-orange-darken-1">{{ group.personGroupName }}</span>
                  <v-chip
                    :color="group.active ? 'success' : 'grey'"
                    size="x-small"
                    variant="flat"
                    class="ml-3 font-weight-bold"
                  >
                    {{ group.active ? 'Активна' : 'Архив' }}
                  </v-chip>
                </div>

                <!-- Действия над группой -->
                <div>
                  <v-btn
                    prepend-icon="mdi-pencil-outline"
                    variant="outlined"
                    color="deep-orange-darken-1"
                    size="small"
                    class="mr-2"
                    @click="openGroupDialog(group)"
                  >
                    Редактировать
                  </v-btn>
                  <v-btn
                    prepend-icon="mdi-delete-outline"
                    variant="outlined"
                    color="error"
                    size="small"
                    @click="confirmDeleteGroup(group.id)"
                  >
                    Удалить
                  </v-btn>
                </div>
              </div>
            </v-card-item>

            <!-- Контентная часть группы -->
            <v-card-text class="pa-4">
              <v-row>
                <!-- Метаданные группы -->
                <v-col cols="12" md="4" class="border-right-md pr-md-4">
                  <div class="mb-2">
                    <span class="text-caption text-grey d-block">Дата создания:</span>
                    <span class="text-body-2 font-weight-medium">{{ formatDateTime(group.personGroupCreateDate) }}</span>
                  </div>
                  <div class="mb-3">
                    <span class="text-caption text-grey d-block">Описание группы:</span>
                    <span class="text-body-2 text-grey-darken-3 font-italic">
                      {{ group.personGroupDescription || 'Описание отсутствует.' }}
                    </span>
                  </div>
                  <v-divider class="my-3"></v-divider>
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-subtitle-2 font-weight-bold text-grey-darken-2">
                      Всего обучающихся:
                    </span>
                    <v-chip color="deep-orange-darken-1" variant="flat" size="small" class="font-weight-bold">
                      {{ group.people ? group.groupPersons.length : 0 }} чел.
                    </v-chip>
                  </div>
                </v-col>

                <!-- Список пользователей/студентов внутри группы (Удобный скроллируемый контейнер) -->
                <v-col cols="12" md="8" class="pl-md-4">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-subtitle-2 font-weight-bold text-grey-darken-2">Состав группы:</span>
                    <v-btn
                      prepend-icon="mdi-account-plus-outline"
                      variant="text"
                      color="success"
                      density="compact"
                      size="small"
                      @click="openAddStudentDialog(group.id)"
                    >
                      Добавить студента
                    </v-btn>
                  </div>

                  <v-card variant="flat" class="border rounded" style="max-height: 200px; overflow-y: auto;">
                    <v-list density="compact" class="pa-0">
                      <v-list-item v-for="(person, idx) in group.groupPersons" :key="person.id" class="border-bottom-light">
                        <template v-slot:prepend>
                          <span class="text-caption text-grey mr-2" style="width: 20px; display: inline-block;">
                            {{ idx + 1 }}.
                          </span>
                          <v-icon size="small" color="grey">mdi-account-outline</v-icon>
                        </template>

                        <v-list-item-title class="text-body-2">

                          {{ person.lastname }} {{ person.firstname }} {{ person.secname || '' }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption text-grey">
                          ID: {{ person.id }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle class="text-caption text-grey">
                          Username: {{ person.username }}
                        </v-list-item-subtitle>

                        <template v-slot:append>
                          <v-btn
                            icon="mdi-close-circle-outline"
                            variant="text"
                            color="error"
                            density="compact"
                            title="Исключить из группы"
                            @click="removeStudentFromGroup(group.id, person.id)"
                          ></v-btn>
                        </template>
                      </v-list-item>

                      <v-list-item v-if="!group.people || group.people.length === 0">
                        <v-list-item-title class="text-center text-caption text-grey py-4 font-italic">
                          В этой группе пока нет ни одного пользователя
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>

          </v-card>
        </v-col>
      </v-row>

      <!-- Заглушка при пустом поиске -->
      <v-alert v-else type="info" variant="tonal" class="mt-2">
        Группы не найдены. Измените поисковый запрос или добавьте новую группу.
      </v-alert>
    </div>

    <!-- СНЭКБАР ДЛЯ СООБЩЕНИЙ -->
    <v-snackbar v-model="snackbar" :timeout="1500">
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn color="pink" variant="text" @click="snackbar = false">Закрыть</v-btn>
      </template>
    </v-snackbar>

    <!-- МОДАЛКА: Создание / Редактирование группы пользователей -->
        <v-dialog v-model="groupDialog" max-width="550px" persistent>
          <v-card >
            <v-card-title class="text-h5 font-weight-bold bg-deep-orange-darken-1">
              {{ groupForm.id ? 'Параметры группы' : 'Создание новой группы' }}
            </v-card-title>

            <v-card-text>
              <v-text-field
                v-model="groupForm.personGroupName"
                label="Название группы*"
                variant="outlined"
                required
                class="mb-3"
              ></v-text-field>

              <v-textarea
                v-model="groupForm.personGroupDescription"
                label="Описание / Примечания к группе"
                variant="outlined"
                rows="3"
                class="mb-3"
                hide-details
              ></v-textarea>

              <v-checkbox
                v-model="groupForm.active"
                label="Группа активна (Разрешить доступ к курсам)"
                color="success"
                hide-details
              ></v-checkbox>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
              <v-spacer></v-spacer>
              <v-btn color="grey-darken-1" variant="text" @click="groupDialog = false">Отмена</v-btn>
              <v-btn
                color="deep-orange-darken-1"
                variant="flat"
                :disabled="!groupForm.personGroupName"
                @click="saveGroup"
              >
                Сохранить
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- МОДАЛКА: Добавление студента в существующую группу -->
        <v-dialog v-model="addStudentDialog" max-width="550px" persistent>
          <v-card class="pa-2">
            <v-card-title class="text-h5 font-weight-bold">
              Добавить пользователя в группу
            </v-card-title>

            <v-card-text>
              <p class="text-body-2 text-grey-darken-2 mb-4">
                Выберите пользователя из общей базы данных системы для включения его в текущий состав группы.
              </p>

              <!-- Умный автокомплит с поиском по мере ввода букв -->
              <v-autocomplete
                v-model="selectedStudentId"
                :items="availableUsers"
                :loading="usersLoading"
                item-title="fullName"
                item-value="id"
                label="Поиск пользователя (ФИО или ID)*"
                placeholder="Начните вводить фамилию..."
                variant="outlined"
                prepend-inner-icon="mdi-account-search-outline"
                hide-details
                required
              ></v-autocomplete>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
              <v-spacer></v-spacer>
              <v-btn color="grey-darken-1" variant="text" @click="addStudentDialog = false">Отмена</v-btn>
              <v-btn
                color="success"
                variant="flat"
                :disabled="!selectedStudentId"
                @click="submitStudentToGroup"
              >
                Включить в состав
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { formatDateTime, debounce } from "@/util/common";
import PageTitle from "@/elements/PageTitle.vue";
import { customAxios } from "@/service/customAxios";
import { usePopupStore } from "@/store/popup";

const { showError } = usePopupStore();

// Реактивные состояния списков данных
const groups = ref([]);
const availableUsers = ref([]);

// Фильтрация и поиск
const searchQuery = ref('');
const debouncedSearchQuery = ref('');

// Состояния модальных окон и снэкбаров
const groupDialog = ref(false);
const addStudentDialog = ref(false);
const usersLoading = ref(false);
const snackbar = ref(false);
const snackbarMessage = ref('');

const currentGroupId = ref(null);
const selectedStudentId = ref(null);

// 1. Меняем модель формы под реальные Java-поля твоей сущности PersonGroup
const groupForm = ref({
  id: null,
  personGroupName: '',        // ИСПРАВЛЕНО: было groupName
  personGroupDescription: '', // ИСПРАВЛЕНО: было groupDescription
  active: true                // или соответствующее поле активности из твоей БД
});

// 2. Исправленный маппинг поиска (filteredGroups) под новое поле
const filteredGroups = computed(() => {
  const query = debouncedSearchQuery.value ? debouncedSearchQuery.value.trim().toLowerCase() : '';
  if (!query) return groups.value;
  return groups.value.filter(g => g.personGroupName && g.personGroupName.toLowerCase().includes(query));
});

// Задержка обновления поискового запроса для оптимизации реактивности
const debounceSearch = debounce(() => {
  debouncedSearchQuery.value = searchQuery.value;
}, 300);
/**
 * 3. Загрузка списка групп
 */
async function loadGroups() {
  try {
    // ИСПРАВЛЕНО: изменен URL на /user_group в соответствии с контроллером
    const r = await customAxios.get('/user_group');
    if (r.status === 200) {
      groups.value = r.data || [];
    }
  } catch (e) {
    showError(e);
  }
}

/**
 * 2. Загрузка общего списка пользователей системы для автокомплита
 */
async function loadAvailableUsers() {
  usersLoading.value = true;
  try {
    const r = await customAxios.get('/person/min-list'); // Легковесный список всех студентов
    if (r.status === 200 && r.data) {
      // Маппим ФИО в единую строку fullName для удобного поиска в v-autocomplete
      availableUsers.value = r.data.map(user => ({
        id: user.id,
        fullName: `${user.lastname || ''} ${user.firstname || ''} ${user.secname || ''}`.trim() + ` (ID: ${user.id})`
      }));
    }
  } catch (e) {
    showError(e);
  } finally {
    usersLoading.value = false;
  }
}

/**
 * 3. Открытие диалога группы (Создание / Редактирование)
 */

function openGroupDialog(group = null) {
  if (group) {
    groupForm.value = {
      id: group.id,
      personGroupName: group.personGroupName,
      personGroupDescription: group.personGroupDescription,
      active: group.active
    };
  } else {
    groupForm.value = { id: null, personGroupName: '', personGroupDescription: '', active: true };
  }
  groupDialog.value = true;
}

/**
 * 4. Сохранение группы (POST или PUT)
 */
async function saveGroup() {
  try {
    if (groupForm.value.id) {
      // ИСПРАВЛЕНО: URL изменен на /user_group/
      await customAxios.put(`/user_group/${groupForm.value.id}`, groupForm.value);
      snackbarMessage.value = "Параметры группы успешно изменены";
    } else {
      // ИСПРАВЛЕНО: URL изменен на /user_group
      await customAxios.post('/user_group', groupForm.value);
      snackbarMessage.value = "Новая группа успешно создана";
    }
    groupDialog.value = false;
    snackbar.value = true;
    await loadGroups();
  } catch (e) {
    showError(e);
  }
}

/**
 * 5. Удаление группы
 */
async function confirmDeleteGroup(groupId) {
  if (!confirm("Вы уверены, что хотите безвозвратно удалить эту группу?")) return;
  try {
    // ИСПРАВЛЕНО: URL изменен на /user_group/
    const r = await customAxios.delete(`/user_group/${groupId}`);
    if (r.status === 200 || r.status === 202) {
      snackbarMessage.value = "Группа успешно удалена";
      snackbar.value = true;
      await loadGroups();
    }
  } catch (e) {
    showError(e);
  }
}

/**
 * 6. Открытие окна включения студента в текущую группу
 */
async function openAddStudentDialog(groupId) {
  currentGroupId.value = groupId;
  selectedStudentId.value = null;
  addStudentDialog.value = true;
  if (availableUsers.value.length === 0) {
    await loadAvailableUsers(); // Подгружаем базу юзеров при первом открытии
  }
}

/**
 * 7. Отправка POST запроса на добавление студента в состав группы
 */
async function submitStudentToGroup() {
  if (!currentGroupId.value || !selectedStudentId.value) return;
  try {
    // ИСПРАВЛЕНО: URL изменен на /user_group/{id}/students в соответствии с GroupApi
    // Payload содержит ключ studentId, который ожидает наш Java Record AddStudentDto
    const r = await customAxios.post(`/user_group/${currentGroupId.value}/students`, {
      studentId: selectedStudentId.value
    });
    if (r.status === 201 || r.status === 200) {
      snackbarMessage.value = "Пользователь успешно добавлен в группу";
      snackbar.value = true;
      addStudentDialog.value = false;
      await loadGroups(); // Перечитываем список, чтобы увидеть изменения
    }
  } catch (e) {
    showError(e);
  }
}

/**
 * 8. Исключение студента из конкретной группы (DELETE запрос)
 */
async function removeStudentFromGroup(groupId, studentId) {
  if (!confirm("Исключить данного пользователя из состава группы?")) return;
  try {
    // ИСПРАВЛЕНО: URL изменен на /user_group/{id}/students/{studentId}
    const r = await customAxios.delete(`/user_group/${groupId}/students/${studentId}`);
    if (r.status === 200 || r.status === 202) {
      snackbarMessage.value = "Пользователь исключен из группы";
      snackbar.value = true;
      await loadGroups();
    }
  } catch (e) {
    showError(e);
  }
}

onMounted(async () => {
  await loadGroups();
});
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
.border-bottom-light {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.border-bottom-light:last-child {
  border-bottom: none;
}
@media (min-width: 960px) {
  .border-right-md {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }
}
</style>